// src/components/website/EventCalendarView.js
// Monthly calendar view of upcoming events for published practitioner websites.

import React, { useState, useMemo, useCallback } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

// ─── Helpers ────────────────────────────────────────────────────────────────

function getEventDates(event) {
  const dates = [];
  const { eventType, schedule } = event;

  if (eventType === 'single' && schedule?.date) {
    dates.push(schedule.date);
  } else if (eventType === 'series' && schedule?.sessionDates?.length > 0) {
    dates.push(...schedule.sessionDates);
  } else if (eventType === 'multi_day' && schedule?.date && schedule?.endDate) {
    const current = new Date(schedule.date + 'T12:00:00');
    const end = new Date(schedule.endDate + 'T12:00:00');
    while (current <= end) {
      dates.push(current.toISOString().split('T')[0]);
      current.setDate(current.getDate() + 1);
    }
  }
  return dates;
}

function buildDateMap(events) {
  const map = {}; // dateStr → [event, event, ...]
  events.forEach(event => {
    getEventDates(event).forEach(dateStr => {
      if (!map[dateStr]) map[dateStr] = [];
      if (!map[dateStr].find(e => e.id === event.id)) {
        map[dateStr].push(event);
      }
    });
  });
  return map;
}

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfWeek(year, month) {
  return new Date(year, month, 1).getDay(); // 0 = Sunday
}

function formatTime12(time24) {
  if (!time24) return '';
  const [h, m] = time24.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${m.toString().padStart(2, '0')} ${ampm}`;
}

function getEventTimeLabel(event) {
  if (event.eventType === 'single' && event.schedule?.startTime) {
    return formatTime12(event.schedule.startTime);
  }
  if (event.eventType === 'series' && event.schedule?.recurringTime) {
    return formatTime12(event.schedule.recurringTime);
  }
  if (event.eventType === 'multi_day' && event.schedule?.dailySchedule?.length > 0) {
    return formatTime12(event.schedule.dailySchedule[0].startTime);
  }
  return '';
}

// ─── Component ──────────────────────────────────────────────────────────────

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const EventCalendarView = ({ events, practitionerSlug }) => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [popover, setPopover] = useState(null); // { dateStr, events, x, y } or null

  const dateMap = useMemo(() => buildDateMap(events), [events]);

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfWeek(year, month);
  const todayStr = now.toISOString().split('T')[0];
  const monthLabel = new Date(year, month).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const goPrev = useCallback(() => {
    if (month === 0) { setYear(y => y - 1); setMonth(11); }
    else setMonth(m => m - 1);
    setPopover(null);
  }, [month]);

  const goNext = useCallback(() => {
    if (month === 11) { setYear(y => y + 1); setMonth(0); }
    else setMonth(m => m + 1);
    setPopover(null);
  }, [month]);

  const handleDayClick = useCallback((dateStr, dayEvents, e) => {
    if (dayEvents.length === 0) return;
    if (dayEvents.length === 1) {
      window.location.href = `/site/${practitionerSlug}/events/${dayEvents[0].slug}`;
      return;
    }
    // Multiple events — show popover
    const rect = e.currentTarget.getBoundingClientRect();
    setPopover({ dateStr, events: dayEvents, x: rect.left, y: rect.bottom });
  }, [practitionerSlug]);

  // Build calendar grid cells
  const cells = [];
  // Empty cells before first day
  for (let i = 0; i < firstDay; i++) {
    cells.push({ day: null, dateStr: null });
  }
  // Day cells
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    cells.push({ day: d, dateStr });
  }

  return (
    <div style={{ position: 'relative' }}>
      {/* Month navigation */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20, marginBottom: 20 }}>
        <span
          role="button"
          tabIndex={0}
          onClick={goPrev}
          onKeyDown={(e) => e.key === 'Enter' && goPrev()}
          style={{ cursor: 'pointer', userSelect: 'none', padding: 6, borderRadius: 6, display: 'flex', alignItems: 'center' }}
        >
          <ChevronLeft size={20} color="#6B7280" />
        </span>
        <span style={{ fontSize: 18, fontWeight: 700, color: '#111827', fontFamily: 'Montserrat, sans-serif', minWidth: 200, textAlign: 'center' }}>
          {monthLabel}
        </span>
        <span
          role="button"
          tabIndex={0}
          onClick={goNext}
          onKeyDown={(e) => e.key === 'Enter' && goNext()}
          style={{ cursor: 'pointer', userSelect: 'none', padding: 6, borderRadius: 6, display: 'flex', alignItems: 'center' }}
        >
          <ChevronRight size={20} color="#6B7280" />
        </span>
      </div>

      {/* Weekday headers */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 4 }}>
        {WEEKDAYS.map(day => (
          <div key={day} style={{
            textAlign: 'center', fontSize: 12, fontWeight: 600, color: '#9CA3AF',
            fontFamily: 'Montserrat, sans-serif', padding: '6px 0', textTransform: 'uppercase',
          }}>
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {cells.map((cell, i) => {
          if (!cell.day) {
            return <div key={`empty-${i}`} style={{ minHeight: 52 }} />;
          }

          const dayEvents = dateMap[cell.dateStr] || [];
          const isToday = cell.dateStr === todayStr;
          const hasEvents = dayEvents.length > 0;

          return (
            <div
              key={cell.dateStr}
              role={hasEvents ? 'button' : undefined}
              tabIndex={hasEvents ? 0 : undefined}
              onClick={hasEvents ? (e) => handleDayClick(cell.dateStr, dayEvents, e) : undefined}
              onKeyDown={hasEvents ? (e) => e.key === 'Enter' && handleDayClick(cell.dateStr, dayEvents, e) : undefined}
              style={{
                minHeight: 52, padding: '6px 4px', textAlign: 'center',
                borderRadius: 8, cursor: hasEvents ? 'pointer' : 'default',
                background: isToday ? '#F5F3FF' : 'transparent',
                transition: 'background 0.15s',
                position: 'relative',
                userSelect: hasEvents ? 'none' : undefined,
              }}
              onMouseEnter={hasEvents ? (e) => { e.currentTarget.style.background = '#F3F4F6'; } : undefined}
              onMouseLeave={hasEvents ? (e) => { e.currentTarget.style.background = isToday ? '#F5F3FF' : 'transparent'; } : undefined}
            >
              <span style={{
                fontSize: 14, fontWeight: isToday ? 700 : 400,
                color: isToday ? '#7c3aed' : '#374151',
                fontFamily: 'Montserrat, sans-serif',
              }}>
                {cell.day}
              </span>

              {/* Event dots */}
              {hasEvents && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: 3, marginTop: 4 }}>
                  {dayEvents.slice(0, 3).map((evt, j) => (
                    <div key={j} style={{
                      width: 6, height: 6, borderRadius: '50%',
                      background: '#7c3aed',
                    }} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Popover for multiple events on one day */}
      {popover && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 50 }}
            onClick={() => setPopover(null)}
          />
          <div style={{
            position: 'absolute', zIndex: 60,
            left: '50%', transform: 'translateX(-50%)',
            top: 'calc(100% + 8px)',
            background: '#FFFFFF', border: '1px solid #E5E7EB',
            borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            padding: 16, minWidth: 260, maxWidth: 320,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#6B7280', fontFamily: 'Montserrat, sans-serif' }}>
                {new Date(popover.dateStr + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
              <span
                role="button"
                tabIndex={0}
                onClick={() => setPopover(null)}
                onKeyDown={(e) => e.key === 'Enter' && setPopover(null)}
                style={{ cursor: 'pointer', userSelect: 'none', padding: 2, display: 'flex' }}
              >
                <X size={14} color="#9CA3AF" />
              </span>
            </div>
            {popover.events.map(evt => (
              <a
                key={evt.id}
                href={`/site/${practitionerSlug}/events/${evt.slug}`}
                style={{
                  display: 'block', padding: '8px 10px', borderRadius: 6,
                  textDecoration: 'none', color: '#111827',
                  marginBottom: 4, transition: 'background 0.1s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#F5F3FF'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ fontSize: 14, fontWeight: 600, fontFamily: 'Montserrat, sans-serif', marginBottom: 2 }}>
                  {evt.title}
                </div>
                <div style={{ fontSize: 12, color: '#6B7280', fontFamily: 'Montserrat, sans-serif' }}>
                  {getEventTimeLabel(evt)}
                </div>
              </a>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default EventCalendarView;
